import { hoursToMilliseconds } from "date-fns";

import { FilterItemBaseInterface } from "../Settings/interfaces";
import {
  InventorySerialTypeEnum,
  InventorySerialTypeTranslatedEnum
} from "../Settings/types/Inventories.types";

import {
  ContractOrigin,
  ContractStatus,
  TranslatedContractCurrentStep,
  TranslatedContractNextStep,
  TranslatedContractOrigin
} from "./enums";

import {
  PaymentMethodType,
  TranslatedPaymentMethodType
} from "@/common/types/PaymentMethod.types";
import {
  PermissionsAction,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { useGetProfile } from "@/core/libs/api/react-query";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { ListDataFiltersTypeEnum } from "@/core/libs/list-data/lib/interfaces/enums";
import {
  ContractShipmentStatus,
  contractShipmentStatusLabels
} from "@/interfaces/modules/contract-shipments";
import { enumToOptions } from "@/utils/EnumToOptions";
import { cpfCnpjFormatter } from "@/utils/FormatCPForCNPJ";
import { formatToSelect } from "@/utils/FormatToSelect/format-to-select";

const removeMask = (value: string) => {
  return value?.replace(/\D/g, "");
};

/*
 * Lista de opções de status de contrato para filtros,
 * não é possível usar a função enumToOptions. Pois o translated
 * envolve 2 enums diferentes dependendo do status.
 */
const contractStatusOptions = [
  {
    key: ContractStatus.IN_PROGRESS,
    text: TranslatedContractCurrentStep.IN_PROGRESS
  },
  {
    key: ContractStatus.DOCUMENTS_SENT,
    text: TranslatedContractNextStep.DOCUMENTS_SENT
  },
  {
    key: ContractStatus.DOCUMENTS_APPROVED,
    text: TranslatedContractNextStep.DOCUMENTS_APPROVED
  },
  {
    key: ContractStatus.CREDIT_APPROVED,
    text: TranslatedContractNextStep.CREDIT_APPROVED
  },
  {
    key: ContractStatus.COMMERCIALLY_APPROVED,
    text: TranslatedContractNextStep.COMMERCIALLY_APPROVED
  },
  {
    key: ContractStatus.CLIENT_SIGNED,
    text: TranslatedContractNextStep.CLIENT_SIGNED
  },
  {
    key: ContractStatus.OPTIONAL_SIGNATURES_COLLECTED,
    text: TranslatedContractNextStep.OPTIONAL_SIGNATURES_COLLECTED
  },
  {
    key: ContractStatus.DELIVERED,
    text: TranslatedContractNextStep.DELIVERED
  },
  {
    key: ContractStatus.DONE,
    text: TranslatedContractCurrentStep.DONE
  },
  {
    key: ContractStatus.TIMED_OUT,
    text: TranslatedContractNextStep.TIMED_OUT
  },
  {
    key: ContractStatus.REJECTED,
    text: TranslatedContractCurrentStep.REJECTED
  },
  {
    key: ContractStatus.CANCELED,
    text: TranslatedContractCurrentStep.CANCELED
  },
  {
    key: ContractStatus.SETUP_PAID,
    text: TranslatedContractCurrentStep.SETUP_PAID
  }
];

const shipmentStatusOptions = [
  { key: "WITHOUT_SHIPMENT", text: "Sem envio ativo" },
  ...enumToOptions(ContractShipmentStatus, contractShipmentStatusLabels)
];

export const useContractFilters = () => {
  const userProfile = useGetProfile();

  const shipmentPermissions =
    userProfile.data?.data?.role.permissions?.[PermissionsScope.POS]?.[
      PermissionsPosModule.CONTRACT_SHIPMENTS
    ];

  const canAccessShipments = (action: PermissionsAction) =>
    shipmentPermissions?.some(
      permission =>
        permission === PermissionsAction.ALL || permission === action
    ) ?? false;

  const canListShipments = canAccessShipments(PermissionsAction.LIST);

  const { data: contractOperations } = useGet<Array<FilterItemBaseInterface>>({
    url: "operations/filters",
    queryToken: "contract-operations-filters",
    cacheTime: hoursToMilliseconds(3)
  });

  const { data: salesTeams } = useGet<Array<FilterItemBaseInterface>>({
    url: "sales-teams/filters",
    queryToken: "contract-sales-teams-filters",
    cacheTime: hoursToMilliseconds(3)
  });

  const { data: users } = useGet<Array<FilterItemBaseInterface>>({
    url: "users/filters",
    queryToken: "contract-users-filters",
    cacheTime: hoursToMilliseconds(3)
  });

  const filters = [
    {
      label: "Código",
      placeholder: "Código do contrato",
      filterQuery: "code",
      type: ListDataFiltersTypeEnum.TEXT,
      inputType: "number"
    },
    {
      label: "Nome",
      placeholder: "Nome do cooperado",
      filterQuery: "fullName",
      type: ListDataFiltersTypeEnum.TEXT
    },
    {
      label: "Documento",
      placeholder: "CPF/CNPJ",
      filterQuery: "documentNumber",
      type: ListDataFiltersTypeEnum.TEXT,
      maskFormatter: cpfCnpjFormatter,
      valueFormatter: removeMask,
      maxLength: 18
    },
    {
      label: "Estado",
      placeholder: "Estado",
      filterQuery: "contractStatus",
      type: ListDataFiltersTypeEnum.SELECT,
      options: contractStatusOptions,
      minWidth: "300px"
    },
    {
      label: "Origem",
      placeholder: "Origem",
      filterQuery: "origin",
      type: ListDataFiltersTypeEnum.SELECT,
      options: enumToOptions(ContractOrigin, TranslatedContractOrigin),
      minWidth: "220px"
    },
    {
      label: "Método de Pagamento",
      placeholder: "Método de Pagamento",
      filterQuery: "setupPaymentMethodType",
      type: ListDataFiltersTypeEnum.SELECT,
      options: enumToOptions(PaymentMethodType, TranslatedPaymentMethodType),
      minWidth: "220px"
    },
    {
      label: "Operação",
      placeholder: "Operação",
      filterQuery: "operation",
      type: ListDataFiltersTypeEnum.SELECT,
      options: formatToSelect(contractOperations) ?? [],
      minWidth: "300px"
    },
    {
      label: "Linha",
      filterQuery: "lineNumber",
      type: ListDataFiltersTypeEnum.MASK_TEXT,
      mask: "(99) 99999-9999",
      valueFormatter: removeMask
    },
    {
      label: "Número de Série",
      placeholder: "Número de Série",
      filterQuery: ListDataFiltersTypeEnum.SERIAL_NUMBER,
      type: ListDataFiltersTypeEnum.TEXT,
      inputType: "number"
    },
    {
      label: "Inventário vinculado",
      placeholder: "Inventário vinculado",
      filterQuery: "inventorySerialType",
      type: ListDataFiltersTypeEnum.SELECT,
      options: enumToOptions(
        InventorySerialTypeEnum,
        InventorySerialTypeTranslatedEnum
      ),
      minWidth: "220px"
    },
    {
      label: "Contratos criados entre",
      filterQuery: ["beginsAt", "finishesAt"],
      type: ListDataFiltersTypeEnum.DATE_RANGE
    },
    {
      label: "Tipo de listagem",
      filterQuery: "me",
      type: ListDataFiltersTypeEnum.SELECT,
      defaultValue: "false",
      options: [
        {
          key: "false",
          text: "Todos os contratos"
        },
        {
          key: "true",
          text: "Apenas os meus contratos"
        }
      ],
      minWidth: "205px"
    },
    {
      label: "Time comercial",
      placeholder: "Time comercial",
      filterQuery: "salesTeam",
      type: ListDataFiltersTypeEnum.SELECT,
      options: formatToSelect(salesTeams) ?? [],
      inputType: "select"
    },
    {
      label: "Usuário",
      placeholder: "Usuário",
      filterQuery: "userId",
      type: ListDataFiltersTypeEnum.SELECT,
      options: formatToSelect(users) ?? [],
      inputType: "select",
      minWidth: "220px"
    },
    {
      label: "Status de Tag",
      placeholder: "Status de Tag",
      filterQuery: "canceledTag",
      type: ListDataFiltersTypeEnum.SELECT,
      options: [{ key: "true", text: "Cancelada", disabled: false }],
      inputType: "select",
      minWidth: "250px"
    },
    ...(canListShipments
      ? [
          {
            label: "Status do Envio",
            placeholder: "Status do Envio",
            filterQuery: "shipmentStatus",
            type: ListDataFiltersTypeEnum.SELECT,
            options: shipmentStatusOptions,
            minWidth: "220px"
          },
          {
            label: "Código do envio",
            placeholder: "Código do envio",
            filterQuery: "shipmentCode",
            type: ListDataFiltersTypeEnum.TEXT
          }
        ]
      : []),
    ...(canAccessShipments(PermissionsAction.LIST_COSTS)
      ? [
          {
            label: "Custo do envio",
            placeholder: "Custo do envio",
            filterQuery: "shipmentCostMissing",
            type: ListDataFiltersTypeEnum.SELECT,

            options: [{ key: "true", text: "Não informado" }],
            minWidth: "220px"
          }
        ]
      : [])
  ];

  return { filters, canListShipments };
};
